
import { Injectable } from '@angular/core';
import { IGeographyNode } from '../../shared/interfaces/geography-node.interface';
import { GeographyMenuOption } from '../../shared/models/geography-menu-option.model';
import { UserGeography } from '../../shared/models/user-geography.model';
import { TreeComponent } from '@circlon/angular-tree-component';
import { CITIES_TIER_ID, CITIES_TYPE_ID, OTHER_REGION_TYPE_ID, PREDEFINED_MENU_OPTIONS, REGION_TYPE_ID, TIER_ZERO, WORLD_TYPE_ID } from '../../shared/constants/country-type.data';
import { COUNTRIES } from '../../shared/constants/countries.data';

@Injectable({
    providedIn: 'root'
})
export class GeographyService {
    private readonly selectAll = `Select all `;
    private readonly deselectAll = `Deselect all `;
    private readonly countriesLabel = `Countries`;
    private readonly worldLabel = `World`;
    private readonly region: [number, number] = [TIER_ZERO, REGION_TYPE_ID];
    private readonly otherRegion: [number, number] = [TIER_ZERO, OTHER_REGION_TYPE_ID];
    private readonly world: [number, number] = [TIER_ZERO, WORLD_TYPE_ID];
    private readonly cities: [number, number] = [CITIES_TIER_ID, CITIES_TYPE_ID];
    private geographyFlatList: Array<IGeographyNode>=[];

    private static setMenuOptionTitle(menuOption: GeographyMenuOption, title?: string): void {
        if (!title) {
            menuOption.title = !menuOption.isSelected
                ? PREDEFINED_MENU_OPTIONS.menuTitle + menuOption.countryName
                : PREDEFINED_MENU_OPTIONS.menuTitleDeselect + menuOption.countryName;
            return;
        }
        menuOption.title = title;
    }

    public arrangeGeographyNodes(
        menu: GeographyMenuOption,
        selectedNode: IGeographyNode,
        userGeographies: ReadonlyArray<UserGeography>
    ): void {
        menu.isSelected = !menu.isSelected;

        if (menu.countryName === this.worldLabel) {
            this.selectDeselectAll(selectedNode, menu);
            return;
        }

        if (menu.countryName === this.countriesLabel) {
            this.selectDeselectAllCountries({ countryNode: selectedNode, menuItem: menu });
            return;
        }

        const isCityRegionOrOtherRegion = (geographyMenuOption: GeographyMenuOption): boolean => {
            const excludes = [this.cities, this.region, this.otherRegion, this.world];
            return excludes.some(
                (tier: [number, number]) => tier[0] === geographyMenuOption.tierId && tier[1] === geographyMenuOption.typeId
            );
        };

        const traverse = (node: IGeographyNode): void => {
            for (const child of node.children) {
                this.selectNodes(child, node, isCityRegionOrOtherRegion, menu, userGeographies);
                traverse(child);
            }
        };
        traverse(selectedNode);
    }

    public iterateGeographyNodesToRefresh(
        nodeToRefresh: IGeographyNode,
        tree: TreeComponent,
        userGeographies: ReadonlyArray<UserGeography>
    ): IGeographyNode {
        if (nodeToRefresh.isSelected && tree && nodeToRefresh.parent) {
            let parentCode = nodeToRefresh.parent;
            while (parentCode) {
                tree.treeModel.getNodeBy((node: any) => node?.data.code === parentCode)?.expand();
                parentCode = tree.treeModel.getNodeBy((node: any) => node.data.code === parentCode)?.data.parent;
            }
        }

        if (nodeToRefresh.children.length === 0) {
            return nodeToRefresh;
        }
        nodeToRefresh = this.refreshGeographyNodeOptions(nodeToRefresh, userGeographies);
        nodeToRefresh.children.forEach((child: IGeographyNode) => this.iterateGeographyNodesToRefresh(child, tree, userGeographies));
        return nodeToRefresh;
    }

    private selectDeselectAllCountries({ countryNode, menuItem }: { countryNode: IGeographyNode; menuItem: GeographyMenuOption }): void {
        const isCityRegionOrOtherRegion = (geographyMenuOption: GeographyMenuOption): boolean => {
            const excludes = [this.cities, this.region, this.otherRegion, this.world];
            return excludes.some(exclude => exclude[0] === geographyMenuOption.tierId && exclude[1] === geographyMenuOption.typeId);
        };

        this.setMenuItem(countryNode.menuOptions, isCityRegionOrOtherRegion, menuItem);

        const countries = Array.from(COUNTRIES).map((o:any) => o.countryCode);

        const traverse = (node: IGeographyNode): void => {
            for (const child of node.children) {
                if (countries.some(c => c === child.code)) {
                    child.isSelected = menuItem.isSelected;
                }
                this.setMenuItem(child.menuOptions, isCityRegionOrOtherRegion, menuItem);
                traverse(child);
            }
        };
        traverse(countryNode);
    }

    private selectDeselectAll(geographyNode: IGeographyNode, menuItem: GeographyMenuOption): void {
        const traverse = (node: IGeographyNode, menuOption: Array<GeographyMenuOption>): void => {
            node.isSelected = menuItem.isSelected;
            for (const option of menuOption) {
                option.isSelected = menuItem.isSelected;
                GeographyService.setMenuOptionTitle(option);
            }
            for (const child of node.children) {
                child.isSelected = menuItem.isSelected;
                traverse(child, node.menuOptions);
            }
        };
        traverse(geographyNode, geographyNode.menuOptions);
    }

    private selectNodes(
        child: IGeographyNode,
        node: IGeographyNode,
        isCityRegionOrOtherRegion: (geographyMenuOption: GeographyMenuOption) => boolean,
        menu: GeographyMenuOption,
        userGeographies?: ReadonlyArray<UserGeography>
    ): void {
        if (
            menu.tierId === child.tierId &&
            menu.typeId === child.typeId &&
            userGeographies?.some(ug => ug.geographyCode === child.code)
        ) {
            child.isSelected = menu.isSelected;
            node.menuOptions.forEach((menuOption: GeographyMenuOption) => {
                if (menuOption.tierId === child.tierId && menuOption.typeId === child.typeId) {
                    menuOption.isSelected = menu.isSelected;
                    menuOption.title = !menuOption.isSelected
                        ? this.selectAll + menuOption.countryName
                        : this.deselectAll + menuOption.countryName;

                    GeographyService.setMenuOptionTitle(menuOption);
                }
            });
            this.toggleCountryState(node, isCityRegionOrOtherRegion);
        }
    }

    private toggleCountryState(node: IGeographyNode, isCityRegionOrOtherRegion: (mi: GeographyMenuOption) => boolean): void {
        const countryOption = node.menuOptions.find(menuOption => menuOption.countryName === this.countriesLabel);
        if (countryOption) {
            const menuOptionsWithOutCountry = node.menuOptions.filter(
                (menuOption: GeographyMenuOption) => menuOption !== countryOption && !isCityRegionOrOtherRegion(menuOption)
            );

            if (menuOptionsWithOutCountry.every((country: GeographyMenuOption) => country.isSelected)) {
                countryOption.isSelected = true;
                GeographyService.setMenuOptionTitle(countryOption);
            }
            if (menuOptionsWithOutCountry.every((country: GeographyMenuOption) => !country.isSelected)) {
                countryOption.isSelected = false;
                GeographyService.setMenuOptionTitle(countryOption);
            }
        }
    }

    private setMenuItem(
        menuOptions: Array<GeographyMenuOption>,
        isCityRegionOrOtherRegion: (geographyMenuOption: GeographyMenuOption) => boolean,
        menuItem: GeographyMenuOption
    ): void {
        for (const option of menuOptions) {
            if (!isCityRegionOrOtherRegion(option)) {
                option.isSelected = menuItem.isSelected;
                GeographyService.setMenuOptionTitle(option);
            }
        }
    }

    private refreshGeographyNodeOptions(currentNode: IGeographyNode, userGeographies: ReadonlyArray<UserGeography>): IGeographyNode {
        this.geographyFlatList = [];
        if (currentNode.children.length > 0) {
            currentNode.menuOptions.forEach((menuOption: GeographyMenuOption) => {
                let isSelected = false;
                this.getGeographyFlatList(currentNode);
                if (menuOption.countryName === this.countriesLabel) {
                    const countries = this.getCountries();
                    const allCountryCodes = countries.map((node: IGeographyNode) => node.code);
                    const selectedCountryCodes = countries
                        .filter((node: IGeographyNode) => node.isSelected === true)
                        .map((node: IGeographyNode) => node.code);

                    isSelected = allCountryCodes.every((code: string) => selectedCountryCodes.includes(code));
                } else if (menuOption.countryName === this.worldLabel) {
                    isSelected = this.geographyFlatList.every((option: IGeographyNode) => option.isSelected === true);
                } else {
                    const filteredNodes = this.geographyFlatList.filter(
                        (option: IGeographyNode) =>
                            option.typeId === menuOption.typeId &&
                            option.tierId === menuOption.tierId &&
                            userGeographies.some((userGeography: UserGeography) => userGeography.geographyCode === option.code)
                    );
                    if (filteredNodes.length > 0) {
                        isSelected = filteredNodes.every((option: IGeographyNode) => option.isSelected === true);
                    }
                }
                if (isSelected) {
                    menuOption.title = this.deselectAll + menuOption.countryName;
                    menuOption.isSelected = isSelected;
                } else {
                    menuOption.title = this.selectAll + menuOption.countryName;
                    menuOption.isSelected = false;
                }
            });
        }
        return currentNode;
    }

    private getGeographyFlatList(node: IGeographyNode): void {
        if (!this.geographyFlatList.includes(node)) {
            this.geographyFlatList.push(node);
        }

        if (node.children) {
            node.children.forEach((option: IGeographyNode) => {
                this.getGeographyFlatList(option);
            });
        }
    }

    private getCountries(): Array<IGeographyNode> {
        return this.geographyFlatList.filter(
            (node: IGeographyNode) =>
                node.tierId !== this.cities[0] &&
                node.typeId !== this.cities[1] &&
                node.tierId !== this.otherRegion[0] &&
                node.typeId !== this.otherRegion[1] &&
                node.tierId !== this.region[0] &&
                node.typeId !== this.region[1] &&
                node.tierId !== this.world[0] &&
                node.typeId !== this.world[1]
        );
    }
}
